// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Button, Icon, Message, Loader, Segment, Checkbox, Popup } from 'semantic-ui-react';
import {
  brokerageIntegrationProviderApi,
  BrokerageIntegrationProvider,
  Provider,
} from '../api/brokerageIntegrationProviderApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import MicrosoftProviderCard from '../components/integrations/MicrosoftProviderCard';
import GoogleProviderCard from '../components/integrations/GoogleProviderCard';

/**
 * Per-brokerage external-integration provider configuration page.
 *
 * Reached via /brokerages/:brokerageId/integrations. Platform-admin-only
 * (the underlying API endpoints return 403 otherwise; this page is only
 * surfaced in admin-ui which platform admins use).
 *
 * Phase 1 shipped the Microsoft card. Phase 2 adds the Google card
 * (sibling component) — the page renders BOTH so a brokerage can have
 * Microsoft, Google, or both configured. Most brokerages have one or
 * the other; mixed-stack brokerages are supported but uncommon.
 */
function BrokerageIntegrationsPage() {
  const navigate = useNavigate();
  const { brokerageId } = useParams<{ brokerageId: string }>();

  const [brokerage, setBrokerage] = useState<Brokerage | null>(null);
  const [providers, setProviders] = useState<BrokerageIntegrationProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Password-login toggle state. Driven by brokerage.passwordLoginEnabled
  // (loaded on mount). Pending=true while the PATCH is in flight so we can
  // disable the toggle and surface any backend error inline.
  const [pwToggleSaving, setPwToggleSaving] = useState(false);
  const [pwToggleError, setPwToggleError] = useState<string | null>(null);

  useEffect(() => {
    if (!brokerageId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const [brokerageResult, providerList] = await Promise.all([
          brokerageApi.getById(brokerageId),
          brokerageIntegrationProviderApi.list(brokerageId),
        ]);
        if (cancelled) return;
        setBrokerage(brokerageResult);
        setProviders(providerList);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load integration providers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [brokerageId]);

  const onProviderUpdated = (updated: BrokerageIntegrationProvider) => {
    setProviders((prev) => {
      const without = prev.filter((p) => p.provider !== updated.provider);
      return [...without, updated];
    });
  };

  const onProviderDeleted = (provider: Provider) => {
    setProviders((prev) => prev.filter((p) => p.provider !== provider));
  };

  const microsoftConfig = providers.find((p) => p.provider === 'MICROSOFT') ?? null;
  const googleConfig = providers.find((p) => p.provider === 'GOOGLE') ?? null;

  // The backend refuses to turn off password login when no SSO provider is
  // enabled — mirror that guard in the UI so the toggle is disabled until
  // at least one provider's SSO is on. Avoids a round-trip + 400 surface.
  const hasEnabledSsoProvider = useMemo(
    () => providers.some((p) => p.ssoEnabled),
    [providers],
  );
  const passwordLoginEnabled = brokerage?.passwordLoginEnabled ?? true;

  const handlePasswordLoginToggle = async () => {
    if (!brokerageId) return;
    const desiredValue = !passwordLoginEnabled;

    // Mirror backend safety net in the UI so we don't even attempt to PATCH
    // when it's guaranteed to 400. The backend stays the source of truth.
    if (!desiredValue && !hasEnabledSsoProvider) {
      setPwToggleError(
        'Enable SSO for at least one provider before disabling password login — ' +
          'otherwise advisors will be locked out.',
      );
      return;
    }

    // Confirm when turning OFF: the consequences (advisors must use SSO,
    // no password fallback) are non-obvious enough to warrant an explicit
    // confirmation. Confirm dialog is fine here — the action is rare.
    if (!desiredValue) {
      const confirmed = globalThis.confirm(
        'Disable password sign-in for this brokerage?\n\n' +
        'Advisors will only be able to sign in via the enabled SSO providers below. ' +
        'Make sure those providers have been validated and tested first.',
      );
      if (!confirmed) return;
    }

    setPwToggleSaving(true);
    setPwToggleError(null);
    try {
      await brokerageApi.setPasswordLoginEnabled(brokerageId, desiredValue);
      // Optimistic local update — re-fetch from server would also work but
      // the PATCH 204s, so we know what the new state is.
      setBrokerage((b) => (b ? { ...b, passwordLoginEnabled: desiredValue } : b));
    } catch (err) {
      setPwToggleError(err instanceof Error ? err.message : 'Failed to toggle password login');
    } finally {
      setPwToggleSaving(false);
    }
  };

  if (!brokerageId) {
    return (
      <Container style={{ padding: '2rem' }}>
        <Message negative>Brokerage id is missing from the URL.</Message>
      </Container>
    );
  }

  return (
    <Container style={{ padding: '2rem' }}>
      <Button onClick={() => navigate(`/brokerages/${brokerageId}/edit`)}>
        <Icon name="arrow left" />
        Back to Brokerage
      </Button>

      <Header as="h1" style={{ marginTop: '1rem' }}>
        <Icon name="key" />
        <Header.Content>
          External Integrations
          {brokerage && <Header.Subheader>{brokerage.name}</Header.Subheader>}
        </Header.Content>
      </Header>

      <Message info>
        <Message.Header>How this works</Message.Header>
        <p>
          The brokerage's IT team registers an OAuth app in their own
          identity tenant (Microsoft Entra or Google Cloud) and sends you
          the credentials via a secure channel. Paste them below, click
          <b> Validate</b> to confirm they work, then <b>Save</b>. Toggling
          <b> SSO</b> on creates the Identity Provider in this brokerage's
          advisor Keycloak realm.
        </p>
        <p>
          Most brokerages run one identity stack — configure that provider's
          card. Mixed-stack brokerages can configure both. The runbooks for
          brokerage IT live at:
        </p>
        <ul>
          <li><i>docs/guides/brokerage-microsoft-app-registration.md</i></li>
          <li><i>docs/guides/brokerage-google-oauth-app-registration.md</i></li>
        </ul>
      </Message>

      {error && (
        <Message negative>
          <Message.Header>Could not load integrations</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading && <Loader active inline="centered" content="Loading…" />}

      {!loading && (
        <>
          <MicrosoftProviderCard
            brokerageId={brokerageId}
            brokerage={brokerage}
            existing={microsoftConfig}
            onUpdated={onProviderUpdated}
            onDeleted={() => onProviderDeleted('MICROSOFT')}
          />
          <GoogleProviderCard
            brokerageId={brokerageId}
            brokerage={brokerage}
            existing={googleConfig}
            onUpdated={onProviderUpdated}
            onDeleted={() => onProviderDeleted('GOOGLE')}
          />

          <Segment data-testid="password-login-toggle">
            <Header as="h2">
              <Icon name="lock" />
              <Header.Content>
                Advisor sign-in methods
                <Header.Subheader>
                  Controls the brokerage's advisor login page
                </Header.Subheader>
              </Header.Content>
            </Header>

            {(() => {
              const toggle = (
                <Checkbox
                  toggle
                  label={
                    passwordLoginEnabled
                      ? 'Password sign-in: ON — advisors can sign in with email + password OR an SSO provider'
                      : 'Password sign-in: OFF — advisors must use an SSO provider'
                  }
                  checked={passwordLoginEnabled}
                  disabled={pwToggleSaving || (passwordLoginEnabled && !hasEnabledSsoProvider)}
                  onChange={handlePasswordLoginToggle}
                />
              );
              // Show a hint tooltip ONLY when the toggle is disabled because
              // no SSO provider is enabled yet — once SSO is on, the toggle
              // is clickable and the tooltip would just be visual noise.
              if (passwordLoginEnabled && !hasEnabledSsoProvider) {
                return (
                  <Popup
                    content="Enable SSO for at least one provider above before disabling password login."
                    trigger={<span>{toggle}</span>}
                  />
                );
              }
              return toggle;
            })()}

            {pwToggleError && (
              <Message negative style={{ marginTop: '1rem' }}>
                <Message.Header>Couldn't update sign-in methods</Message.Header>
                <p>{pwToggleError}</p>
              </Message>
            )}

            <p style={{ marginTop: '1rem', color: '#666' }}>
              When OFF, the advisor login page hides the email/password form and
              shows only the enabled SSO provider buttons. The platform refuses
              to disable password sign-in when no SSO provider is enabled, so
              advisors can never be locked out by mistake.
            </p>
          </Segment>
        </>
      )}
    </Container>
  );
}

export default BrokerageIntegrationsPage;
